/**
 * O telhado do prédio, como se vê do 1.º andar.
 *
 * Lá em cima só há salas por cima de uma parte do prédio (o gabinete do CEO e a
 * antessala). O resto da planta do rés-do-chão fica debaixo de TELHADO — e é o
 * telhado que tapa, a quem subiu, as salas e as pessoas lá de baixo. A seu lado
 * continua o jardim de sempre, que o cliente mostra por uma segunda câmara.
 *
 * Vive aqui, puro e sem Phaser, porque há dois donos a desenhá-lo: a cena e o
 * minimapa. Tudo em tiles; as peças do telhado em tiles da SUPERFÍCIE (o desvio
 * do andar soma quem desenha), com fracções, porque um painel solar não mede
 * um número inteiro de tiles.
 */
import { BUILDING, floorAt, isUpstairs, UPSTAIRS_OFFSET_ROWS } from "@greenvoice/shared";

/** Este tile do 1.º andar tem o prédio por baixo? (Por cima do jardim não há nada.) */
export function sobreOPredio(col: number, row: number): boolean {
  if (!isUpstairs(row)) return false;
  const baixo = row - UPSTAIRS_OFFSET_ROWS;
  return (
    col >= BUILDING.col && col < BUILDING.col + BUILDING.w && baixo >= BUILDING.row && baixo < BUILDING.row + BUILDING.h
  );
}

/** Há chão do 1.º andar encostado a este tile (8 vizinhos)? Então é parede, não telhado. */
function encostaAoAndar(col: number, row: number): boolean {
  for (let dr = -1; dr <= 1; dr += 1) {
    for (let dc = -1; dc <= 1; dc += 1) {
      if ((dr !== 0 || dc !== 0) && floorAt(col + dc, row + dr) != null) return true;
    }
  }
  return false;
}

/** Este tile do 1.º andar é telhado à vista: prédio por baixo, sem sala nem parede por cima. */
export function eTelhado(col: number, row: number): boolean {
  return sobreOPredio(col, row) && floorAt(col, row) == null && !encostaAoAndar(col, row);
}

export type TipoDePecaDoTelhado = "solar" | "ac" | "heliponto";

/** Canto superior-esquerdo, em tiles da superfície. */
export interface PecaDoTelhado {
  readonly tipo: TipoDePecaDoTelhado;
  readonly col: number;
  readonly row: number;
}

/** A medida de cada peça, em tiles. É a da arte (`officeArt`): 64×40, 40×36 e 208×208 px. */
export const MEDIDA_DA_PECA: Record<TipoDePecaDoTelhado, { w: number; h: number }> = {
  solar: { w: 2, h: 1.25 },
  ac: { w: 1.25, h: 1.125 },
  heliponto: { w: 6.5, h: 6.5 },
};

/**
 * Painéis solares em dois campos, um de cada lado do bloco do CEO, e três
 * máquinas de ar condicionado na faixa do corredor. Os campos são grelhas
 * regulares de propósito: é a regularidade que diz "telhado" à primeira vista.
 *
 * À direita do gabinete do CEO, no lugar da metade de cima do campo de painéis
 * de leste, fica o heliponto.
 */
export const PECAS_DO_TELHADO: readonly PecaDoTelhado[] = (() => {
  const out: PecaDoTelhado[] = [];
  const colunas = { oeste: [4, 6.25, 8.5], este: [25, 27.25, 29.5] };
  const filas = [4.5, 6, 7.5, 9, 15.5, 17, 18.5, 20];
  const filasDoHeliponto = new Set([4.5, 6, 7.5, 9]);
  for (const lado of [colunas.oeste, colunas.este]) {
    for (const row of filas) {
      if (lado === colunas.este && filasDoHeliponto.has(row)) continue;
      for (const col of lado) out.push({ tipo: "solar", col, row });
    }
  }
  out.push({ tipo: "heliponto", col: 25.75, row: 4.25 });
  out.push({ tipo: "ac", col: 5, row: 11.5 }, { tipo: "ac", col: 9.5, row: 11.5 }, { tipo: "ac", col: 29, row: 11.5 });
  return out;
})();
